import type { Story } from '@/types/content';
import { Link } from 'react-router-dom';

interface StoryCardProps {
  story: Story;
  variant?: 'default' | 'wide' | 'compact';
  index?: number;
}

export default function StoryCard({ story, variant = 'default', index = 0 }: StoryCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/stories/${story.id}`}
        className="group flex items-center gap-4 py-4 border-b border-background-200 last:border-b-0"
      >
        <span className="font-heading text-2xl font-bold text-background-400 w-8 shrink-0 group-hover:text-primary-500 transition-colors">
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
            {story.title}
          </h3>
          <p className="mt-1 text-xs text-foreground-500">
            {story.categoryName} · 阅读 {Math.round(story.views / 100) / 10}k
          </p>
        </div>
        <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/stories/${story.id}`}
        className="group grid grid-cols-1 md:grid-cols-2 gap-5 rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
      >
        <div className="relative w-full h-52 md:h-full md:min-h-[220px] overflow-hidden">
          <img
            src={story.cover}
            alt={`${story.title} - ${story.categoryName}高校吃瓜故事配图`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5 md:p-6 flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
            {story.categoryName}
          </span>
          <h3 className="mt-3 font-heading text-xl md:text-2xl font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">
            {story.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 line-clamp-2">{story.excerpt}</p>
          <div className="mt-4 flex items-center gap-3 text-xs text-foreground-500">
            <span>{story.author}</span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span>{story.date}</span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span>{story.readTime} 分钟</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group h-full">
      <Link to={`/stories/${story.id}`} className="flex h-full flex-col">
        <div className="relative w-full h-48 overflow-hidden rounded-lg">
          <img
            src={story.cover}
            alt={`${story.title} - 高校吃瓜趣闻配图`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {story.hot && (
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
              <i className="ri-fire-line" /> 热瓜
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col pt-4">
          <span className="text-xs font-medium text-accent-700">{story.categoryName}</span>
          <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {story.title}
          </h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-2">{story.excerpt}</p>
          <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
            <span>{story.date}</span>
            <span className="w-1 h-1 rounded-full bg-background-400" />
            <span>{story.readTime} 分钟</span>
          </div>
        </div>
      </Link>
    </article>
  );
}